import { SaveOutlined } from '@ant-design/icons'
import { Button, Form, Input, message, Modal } from 'antd'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { useCreatePropertyListMutation } from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import { useToggleState } from '../../utils/useToggleState'

export interface PropertyListCreateModalButtonProps {
  enabled: boolean
  buttonClassName?: string
}

const placeholder = {
  [PROPERTY_TYPES.AREA]: {
    name: 'Např. Pozemky nad 500 m² - Praha',
    description:
      'Např. pozemky určené k výstavbě, plocha nad 500 m², Praha a okolí',
  },
  [PROPERTY_TYPES.BUILDING]: {
    name: 'Např. RD nad 200 m² - Opava',
    description:
      'Např. rodinné domy, plocha 100 až 200 m², 2 patra, bez plynu, okres Opava',
  },
}
export const PropertyListCreateModalButton: FunctionComponent<PropertyListCreateModalButtonProps> =
  props => {
    const { tenantId, userId } = useAppSelector(state => state.myProfile)
    const cadastralFilter = useAppSelector(store => store.cadastralFilter)

    const [isOpen, toggleOpen] = useToggleState(false)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')

    const [, createPropertyList] = useCreatePropertyListMutation()

    const canShow = useMemo(
      () =>
        props.enabled &&
        !!cadastralFilter?.whereClause &&
        !!cadastralFilter?.propertyType,
      [
        props.enabled,
        cadastralFilter?.whereClause,
        cadastralFilter?.propertyType,
      ]
    )

    const okButtonProps = useMemo(
      () => ({ disabled: name.trim().length === 0 }),
      [name]
    )

    const descriptionFromFilter = useMemo(() => {
      if (!cadastralFilter.whereClause) return ''
      try {
        const whereClause = JSON.parse(cadastralFilter.whereClause)
        const geometry = whereClause?.geometry ? 'v okrese = ANO \n' : ''
        return geometry + whereClause?.where || ''
      } catch {
        return ''
      }
    }, [cadastralFilter.whereClause])

    useEffect(() => {
      if (isOpen && descriptionFromFilter) {
        setDescription(descriptionFromFilter)
      }
    }, [isOpen, descriptionFromFilter])

    const handleSave = useCallback(async () => {
      if (!cadastralFilter.whereClause || !cadastralFilter.propertyType) return
      const variables = {
        name: name.trim(),
        description: description.trim() || null,
        whereClause: cadastralFilter.whereClause,
        count: cadastralFilter.count || 0,
        propertyType: cadastralFilter.propertyType,
        accountId: tenantId,
        accountUserId: userId,
      }

      try {
        const result = await createPropertyList(variables)
        if (result.error) {
          message.error('Nepodařilo se uložit seznam nemovitostí')
        }
        message.success('Seznam nemovitostí byl úspěšně uložen')
        toggleOpen()
        setName('')
        setDescription('')
      } catch (e) {
        message.error('Nepodařilo se uložit seznam nemovitostí')
      }
    }, [
      cadastralFilter,
      name,
      description,
      tenantId,
      userId,
      createPropertyList,
      toggleOpen,
    ])

    if (!canShow) return null

    return (
      <>
        <Button
          icon={<SaveOutlined />}
          onClick={toggleOpen}
          className={props.buttonClassName}
          title="Uložit seznam nemovitostí"
        />
        {isOpen && (
          <Modal
            title="Uložit seznam nemovitostí"
            open={isOpen}
            onOk={handleSave}
            onCancel={toggleOpen}
            okText="Uložit"
            cancelText="Zrušit"
            okButtonProps={okButtonProps}
          >
            <Form layout="vertical">
              <Form.Item label="Název" required>
                <Input
                  placeholder={
                    placeholder[
                      cadastralFilter.propertyType as keyof typeof placeholder
                    ]?.name
                  }
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </Form.Item>
              <Form.Item label="Popis">
                <Input.TextArea
                  rows={3}
                  placeholder={
                    placeholder[
                      cadastralFilter.propertyType as keyof typeof placeholder
                    ]?.description
                  }
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                />
              </Form.Item>
            </Form>
          </Modal>
        )}
      </>
    )
  }
